import {
  Box,
  Grid,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Fade,
  IconButton,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DiamondIcon from "@mui/icons-material/Diamond";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LockIcon from "@mui/icons-material/Lock";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import "../Tools.css";

// Array containing the instructions
const instructions = [
  {
    title: "What is Staking?",
    content:
      "Staking allows you to lock up your tokens in a pool to earn rewards. Rewards are calculated based on the pool's APR and the amount of tokens staked.",
  },
  {
    title: "How to Stake?",
    content:
      "Choose a pool, enter the amount you wish to stake, and confirm the transaction. Ensure you have enough tokens in your wallet before staking. You can only stake in one pool at a time (temporary restriction).",
  },
  {
    title: "Rewards and APR",
    content:
      "Rewards are distributed daily based on the pool's Annual Percentage Rate (APR). The longer you stake, the more rewards you earn.",
  },
  {
    title: "Unstaking",
    content:
      "You can unstake your tokens anytime unless a lock period is active. Note that rewards stop accruing after unstaking.",
  },
  {
    title: "Leaderboards",
    content:
      "Leaderboards show the top stakers in each pool based on their staked amount and duration. Compete to secure your rank! Extra rewards such as weekly or monthly NFT airdrops will be sent to random stakers!",
  },
];

interface InstructionsModalProps {
  open: boolean;
  onClose: () => void;
}

export const InstructionsModal = ({ open, onClose }: InstructionsModalProps) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          background: "linear-gradient(135deg, #3a2609 0%, #5c3c10 100%)",
          borderRadius: "16px",
          border: "4px solid #f5e1a3",
          position: "relative",
          overflow: "hidden",
        }
      }}
    >
      <Box className="wood-texture-overlay" />
      
      <DialogTitle sx={{ 
        color: "#f5e1a3", 
        fontWeight: "bold",
        textAlign: "center",
        fontSize: "1.8rem",
        textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
        paddingBottom: 1,
        position: "relative"
      }}>
        Staking Instructions
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#f5e1a3",
            "&:hover": {
              backgroundColor: "rgba(245, 225, 163, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider sx={{ 
        borderColor: "rgba(245, 225, 163, 0.3)",
        width: "90%",
        margin: "0 auto",
      }} />
      
      <DialogContent sx={{ padding: 3, position: "relative" }}>
        <Paper elevation={0} sx={{
          backgroundColor: "rgba(0, 0, 0, 0.25)",
          borderRadius: "12px",
          padding: 2,
          border: "1px solid rgba(245, 225, 163, 0.2)",
          marginBottom: 3
        }}>
          <Typography variant="body1" sx={{ 
            color: "#f5e1a3", 
            marginBottom: 2,
            fontWeight: "bold"
          }}>
            <InfoIcon sx={{ verticalAlign: "middle", marginRight: 1 }} />
            Welcome to the Staking Area
          </Typography>
          <Typography variant="body2" sx={{ color: "white", marginBottom: 1 }}>
            Stake your ANTLERS tokens to earn rewards over time. Different pools offer various APRs and lock periods.
            Choose the pool that best fits your strategy!
          </Typography>
        </Paper>
        
        <List sx={{ padding: 0 }}>
          <ListItem sx={{ 
            borderRadius: "8px", 
            mb: 1.5, 
            backgroundColor: "rgba(245, 225, 163, 0.05)",
            border: "1px solid rgba(245, 225, 163, 0.1)",
            padding: 2
          }}>
            <ListItemIcon sx={{ color: "#f5e1a3", minWidth: "40px" }}>
              <MonetizationOnIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                  What is Staking?
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ color: "white" }}>
                  Staking is the process of locking your ANTLERS tokens in a pool to earn rewards. 
                  The longer you stake, the more rewards you earn.
                </Typography>
              }
            />
          </ListItem>
          
          <ListItem sx={{ 
            borderRadius: "8px", 
            mb: 1.5, 
            backgroundColor: "rgba(245, 225, 163, 0.05)",
            border: "1px solid rgba(245, 225, 163, 0.1)",
            padding: 2
          }}>
            <ListItemIcon sx={{ color: "#f5e1a3", minWidth: "40px" }}>
              <LockIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                  Lock Periods
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ color: "white" }}>
                  Each pool has a lock period during which your tokens cannot be unstaked. 
                  Higher tier pools typically have longer lock periods but offer better rewards.
                </Typography>
              }
            />
          </ListItem>
          
          <ListItem sx={{ 
            borderRadius: "8px", 
            mb: 1.5, 
            backgroundColor: "rgba(245, 225, 163, 0.05)",
            border: "1px solid rgba(245, 225, 163, 0.1)",
            padding: 2
          }}>
            <ListItemIcon sx={{ color: "#f5e1a3", minWidth: "40px" }}>
              <ArrowUpwardIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                  APR & Monthly Increases
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ color: "white" }}>
                  Annual Percentage Rate (APR) determines your rewards. Some pools feature monthly 
                  increases to APR, boosting your returns over time.
                </Typography>
              }
            />
          </ListItem>
          
          <ListItem sx={{ 
            borderRadius: "8px", 
            mb: 1.5, 
            backgroundColor: "rgba(245, 225, 163, 0.05)",
            border: "1px solid rgba(245, 225, 163, 0.1)",
            padding: 2
          }}>
            <ListItemIcon sx={{ color: "#f5e1a3", minWidth: "40px" }}>
              <DiamondIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                  Claiming Rewards
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ color: "white" }}>
                  You can claim your rewards once every 24 hours. Rewards continue to accrue whether claimed or not,
                  but claiming regularly may help track your earnings.
                </Typography>
              }
            />
          </ListItem>
          
          <ListItem sx={{ 
            borderRadius: "8px", 
            mb: 1.5, 
            backgroundColor: "rgba(245, 225, 163, 0.05)",
            border: "1px solid rgba(245, 225, 163, 0.1)",
            padding: 2
          }}>
            <ListItemIcon sx={{ color: "#f5e1a3", minWidth: "40px" }}>
              <EmojiEventsIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                  Leaderboards
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ color: "white" }}>
                  Compete with other stakers on the leaderboard! Higher staked amounts move you up the ranks,
                  potentially earning you bonus rewards in the future.
                </Typography>
              }
            />
          </ListItem>
          
          <ListItem sx={{ 
            borderRadius: "8px", 
            mb: 1.5, 
            backgroundColor: "rgba(245, 225, 163, 0.05)",
            border: "1px solid rgba(245, 225, 163, 0.1)",
            padding: 2
          }}>
            <ListItemIcon sx={{ color: "#f5e1a3", minWidth: "40px" }}>
              <AccessTimeIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                  Pro Tip: Long-term Strategy
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ color: "white" }}>
                  For maximum rewards, select pools with monthly APR increases and longer lock periods.
                  The longer you stay staked, the more your returns compound!
                </Typography>
              }
            />
          </ListItem>
        </List>
      </DialogContent>
    </Dialog>
  );
};
